import React, { useState, useEffect, useRef } from "react";
import { Container, Form, Button, Spinner, Alert } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { Editor } from "@toast-ui/react-editor";
import "@toast-ui/editor/dist/toastui-editor.css";
import axiosInstance from "../api/axiosInstance";
import useAuthStore from "../stores/authStore";
import useDiaryStore from "../stores/diaryStore";

const DiaryWrite = () => {
    const navigate = useNavigate();
    const { id } = useParams(); // id가 존재하면 수정 모드
    const { user } = useAuthStore();
    const editorRef = useRef();

    const [title, setTitle] = useState("");
    const [initialContent, setInitialContent] = useState(""); // 초기 내용 상태
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(!!id);
    const [error, setError] = useState("");

    // 관리자 권한 확인
    useEffect(() => {
        if (user && user.role !== 'admin') {
            alert("관리자만 접근 가능합니다.");
            navigate("/");
        }
    }, [user, navigate]);

    // 수정 모드일 경우 데이터 조회
    useEffect(() => {
        if (id) {
            const fetchPost = async () => {
                try {
                    // 상세 조회를 위해 공개 API 사용
                    const response = await axiosInstance.get(`postDetail?post_id=${id}`);
                    const post = response.data.data.postList[0]?.json;
                    if (post) {
                        setTitle(post.title);
                        setInitialContent(post.content); // 초기 내용 상태 설정
                    } else {
                        setError("게시글을 찾을 수 없습니다.");
                    }
                } catch (err) {
                    console.error(err);
                    setError("게시글 불러오기 실패");
                } finally {
                    setFetching(false);
                }
            };
            fetchPost();
        }
    }, [id]);

    const handleSubmit = async () => {
        const content = editorRef.current.getInstance().getMarkdown();
        if (!title.trim() || !content.trim()) {
            alert("제목과 내용을 입력해주세요.");
            return;
        }

        setLoading(true);
        try {
            if (id) {
                // 수정
                await axiosInstance.put("posts", {
                    id: parseInt(id), // ID가 정수인지 확인
                    title,
                    content,
                    is_notice: false // 요청에 따라 기본값 false
                });
                alert("수정되었습니다.");
            } else {
                // 생성
                await axiosInstance.post("posts", {
                    slug: "admin-notice", // 요청에 따라 고정값
                    title,
                    content,
                    is_notice: false
                });
                alert("등록되었습니다.");
            }
            // 글 작성/수정 후에는 스토어 초기화 (새로 불러오기 위함)
            useDiaryStore.getState().reset();
            navigate("/");
        } catch (err) {
            console.error("Save Error:", err);
            // 가능한 경우 더 상세한 에러 표시
            const errMsg = err.response?.data?.message || err.message || "저장 중 오류가 발생했습니다.";
            alert(`오류가 발생했습니다: ${errMsg}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm("정말 삭제하시겠습니까?")) return;

        setLoading(true);
        try {
            await axiosInstance.delete("posts", {
                data: { id: id } // 삭제 시 config에 data 포함 필요
            });
            alert("삭제되었습니다.");
            // 글 삭제 후에는 스토어 초기화
            useDiaryStore.getState().reset();
            navigate("/");
        } catch (err) {
            console.error(err);
            alert("삭제 중 오류가 발생했습니다.");
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" />
            </Container>
        );
    }

    return (
        <Container className="py-5" style={{ maxWidth: '1000px' }}>
            <h2 className="mb-4 fw-bold">{id ? "일기 수정" : "일기 작성"}</h2>

            {error && <Alert variant="danger">{error}</Alert>}

            <Form.Group className="mb-3">
                <Form.Label>제목</Form.Label>
                <Form.Control
                    type="text"
                    placeholder="제목을 입력하세요"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                />
            </Form.Group>

            <div className="mb-4">
                <Editor
                    initialValue={initialContent || " "} // 상태 사용, 문제 방지를 위해 비어있으면 공백 기본값
                    previewStyle="vertical"
                    height="600px"
                    initialEditType="markdown"
                    useCommandShortcut={true}
                    ref={editorRef}
                    hooks={{
                        addImageBlobHook: async (blob, callback) => {
                            try {
                                const formData = new FormData();
                                formData.append('attachment', blob);

                                const response = await axiosInstance.post('attachments', formData, {
                                    headers: {
                                        'Content-Type': 'multipart/form-data'
                                    }
                                });

                                if (response.data.status === "0000") {
                                    const imageUrl = response.data.data.fileName;
                                    callback(imageUrl, 'image');
                                } else {
                                    console.error('Upload failed:', response.data);
                                    alert('이미지 업로드에 실패했습니다.');
                                }
                            } catch (error) {
                                console.error('Error uploading image:', error);
                                alert('이미지 업로드 중 오류가 발생했습니다.');
                            }
                        }
                    }}
                />
            </div>

            <div className="d-flex justify-content-end gap-2">
                <Button variant="secondary" onClick={() => navigate("/")}>
                    취소
                </Button>
                {id && (
                    <Button variant="danger" onClick={handleDelete} disabled={loading}>
                        삭제
                    </Button>
                )}
                <Button variant="primary" onClick={handleSubmit} disabled={loading}>
                    {loading ? <Spinner size="sm" animation="border" /> : (id ? "수정" : "등록")}
                </Button>
            </div>
        </Container>
    );
};

export default DiaryWrite;
